import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Input,
  Textarea,
  Select,
  Checkbox,
  CheckboxGroup,
  Stack,
  useColorModeValue,
  useToast,
  Progress,
  Spinner,
  Box,
  Image,
  IconButton,
  Badge
} from '@chakra-ui/react';
import { FiUpload, FiX, FiSave } from 'react-icons/fi';
import IconWrapper from './IconWrapper';
import { updateContent, getContentForEdit, ContentUpdateData } from '../services/contentUpdateService';
import { GENRES } from '../constants/genres';

interface EditContentModalProps {
  isOpen: boolean;
  onClose: () => void;
  contentId: string;
  userId: string;
  onSuccess: () => void;
}

const EditContentModal: React.FC<EditContentModalProps> = ({
  isOpen,
  onClose,
  contentId,
  userId,
  onSuccess
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [content, setContent] = useState<any>(null);
  const [updateData, setUpdateData] = useState<ContentUpdateData>({});
  const [previewUrls, setPreviewUrls] = useState<{ [key: string]: string | { name: string } }>({});
  const toast = useToast();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.900', 'white');

  // Load content data when modal opens
  useEffect(() => {
    if (isOpen && contentId) {
      loadContent();
    }
  }, [isOpen, contentId]);

  const loadContent = async () => {
    setIsLoading(true);
    try {
      const contentData = await getContentForEdit(contentId, userId);
      setContent(contentData);
      setUpdateData({
        title: contentData.title || '',
        description: contentData.description || '',
        genre: contentData.genre || '',
        genres: contentData.genres || [],
        tags: contentData.tags || [],
        contentRating: contentData.contentRating || 'G',
        language: contentData.language || 'English',
        languages: contentData.languages || [],
        year: contentData.year || new Date().getFullYear(),
        duration: contentData.duration || 0,
        visibility: contentData.visibility || 'public',
        cast: contentData.cast || [],
        director: contentData.director || [],
        writer: contentData.writer || [],
        contentAdvisory: contentData.contentAdvisory || []
      });
    } catch (error) {
      console.error('Error loading content:', error);
      toast({
        title: 'Error',
        description: 'Failed to load content for editing.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      onClose();
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof ContentUpdateData, value: any) => {
    setUpdateData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileUpload = (field: keyof ContentUpdateData, file: File) => {
    setUpdateData(prev => ({
      ...prev,
      [field]: file
    }));
    
    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setPreviewUrls(prev => ({
      ...prev,
      [field]: previewUrl
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const result = await updateContent(contentId, userId, updateData);
      
      if (result.success) {
        toast({
          title: 'Content Updated',
          description: result.message,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onSuccess();
        onClose();
      } else {
        toast({
          title: 'Update Failed',
          description: result.message,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error updating content:', error);
      toast({
        title: 'Update Failed',
        description: 'An unexpected error occurred.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const genres = GENRES;
  const contentRatings = ['G', 'PG', 'PG-13', 'R', 'NC-17'];
  const languages = ['English', 'Spanish', 'French', 'German', 'Chinese', 'Japanese'];

  if (isLoading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalBody>
            <Box display="flex" justifyContent="center" alignItems="center" py={8}>
              <Spinner size="xl" />
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent maxH="90vh" overflowY="auto">
        <ModalHeader>Edit Content</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={6} align="stretch">
            {/* Basic Information */}
            <VStack spacing={4} align="stretch">
              <Text fontSize="lg" fontWeight="bold">Basic Information</Text>
              
              <VStack align="stretch">
                <Text fontWeight="medium">Title</Text>
                <Input
                  value={updateData.title || ''}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Content title"
                />
              </VStack>

              <VStack align="stretch">
                <Text fontWeight="medium">Description</Text>
                <Textarea
                  value={updateData.description || ''}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Content description"
                  rows={4}
                />
              </VStack>

              <VStack align="stretch">
                <Text fontWeight="medium">Genres (Select up to 3)</Text>
                <HStack gap={2} wrap="wrap">
                  {genres.map((genre) => (
                    <Button
                      key={genre}
                      size="sm"
                      variant={updateData.genres?.includes(genre) ? 'solid' : 'outline'}
                      colorScheme={updateData.genres?.includes(genre) ? 'purple' : 'gray'}
                      onClick={() => {
                        const currentGenres = updateData.genres || [];
                        const isSelected = currentGenres.includes(genre);
                        
                        if (isSelected) {
                          handleInputChange('genres', currentGenres.filter(g => g !== genre));
                        } else if (currentGenres.length < 3) {
                          handleInputChange('genres', [...currentGenres, genre]);
                        }
                      }}
                      isDisabled={!updateData.genres?.includes(genre) && (updateData.genres?.length || 0) >= 3}
                    >
                      {genre}
                    </Button>
                  ))}
                </HStack>
                <Text fontSize="sm" color="gray.500">
                  Selected: {updateData.genres?.join(', ') || 'None'}
                </Text>
              </VStack>

              <HStack spacing={4}>
                <VStack align="stretch" flex="1">
                  <Text fontWeight="medium">Content Rating</Text>
                  <Select
                    value={updateData.contentRating || ''}
                    onChange={(e) => handleInputChange('contentRating', e.target.value)}
                  >
                    {contentRatings.map(rating => (
                      <option key={rating} value={rating}>{rating}</option>
                    ))}
                  </Select>
                </VStack>
                <VStack align="stretch" flex="1">
                  <Text fontWeight="medium">Language</Text>
                  <Select
                    value={updateData.language || ''}
                    onChange={(e) => handleInputChange('language', e.target.value)}
                  >
                    {languages.map(lang => (
                      <option key={lang} value={lang}>{lang}</option>
                    ))}
                  </Select>
                </VStack>

                <VStack align="stretch" flex="1">
                  <Text fontWeight="medium">Year</Text>
                  <Input
                    type="number"
                    value={updateData.year || ''}
                    onChange={(e) => handleInputChange('year', parseInt(e.target.value))}
                    placeholder="2024"
                  />
                </VStack>
              </HStack>
            </VStack>

            {/* Additional Fields for Books/Comics */}
            {content?.category === 'book' && (
              <VStack spacing={4} align="stretch">
                <Text fontSize="lg" fontWeight="bold">Book/Comic Details</Text>
                
                <VStack align="stretch">
                  <Text fontWeight="medium">Cast</Text>
                  <Input
                    value={Array.isArray(updateData.cast) ? updateData.cast.join(', ') : updateData.cast || ''}
                    onChange={(e) => handleInputChange('cast', e.target.value.split(',').map(item => item.trim()))}
                    placeholder="Enter names separated by commas"
                  />
                </VStack>

                <VStack align="stretch">
                  <Text fontWeight="medium">Writer Name</Text>
                  <Input
                    value={Array.isArray(updateData.writer) ? updateData.writer.join(', ') : updateData.writer || ''}
                    onChange={(e) => handleInputChange('writer', e.target.value.split(',').map(item => item.trim()))}
                    placeholder="Enter names separated by commas"
                  />
                </VStack>

                <VStack align="stretch">
                  <Text fontWeight="medium">Content Advisory</Text>
                  <Input
                    value={Array.isArray(updateData.contentAdvisory) ? updateData.contentAdvisory.join(', ') : updateData.contentAdvisory || ''}
                    onChange={(e) => handleInputChange('contentAdvisory', e.target.value.split(',').map(item => item.trim()))}
                    placeholder="Enter content warnings separated by commas"
                  />
                </VStack>

                <VStack align="stretch">
                  <Text fontWeight="medium">Languages</Text>
                  <Input
                    value={Array.isArray(updateData.languages) ? updateData.languages.join(', ') : updateData.languages || ''}
                    onChange={(e) => handleInputChange('languages', e.target.value.split(',').map(item => item.trim()))}
                    placeholder="Enter languages separated by commas"
                  />
                </VStack>

                <VStack align="stretch">
                  <Text fontWeight="medium">Tags</Text>
                  <Input
                    value={Array.isArray(updateData.tags) ? updateData.tags.join(', ') : updateData.tags || ''}
                    onChange={(e) => handleInputChange('tags', e.target.value.split(',').map(item => item.trim()))}
                    placeholder="Enter tags separated by commas"
                  />
                </VStack>

                <VStack align="stretch">
                  <Text fontWeight="medium">Visibility</Text>
                  <Select
                    value={updateData.visibility || 'public'}
                    onChange={(e) => handleInputChange('visibility', e.target.value)}
                  >
                    <option value="public">Public</option>
                    <option value="unlisted">Unlisted</option>
                    <option value="private">Private</option>
                  </Select>
                </VStack>
              </VStack>
            )}

            {/* File Uploads */}
            <VStack spacing={4} align="stretch">
              <Text fontSize="lg" fontWeight="bold">Media Files</Text>
              
              {/* Comic/Book File Upload */}
              <VStack align="stretch">
                <Text fontWeight="medium">Comic/Book File</Text>
                <Box
                  border="2px dashed"
                  borderColor={borderColor}
                  borderRadius="md"
                  p={4}
                  textAlign="center"
                >
                  {previewUrls.bookFile ? (
                    <VStack>
                      <Text fontSize="sm" color="green.500" fontWeight="medium">File Selected</Text>
                      <Text fontSize="xs" color="gray.500">
                        {typeof previewUrls.bookFile === 'object' ? previewUrls.bookFile.name : 'File Selected'}
                      </Text>
                      <HStack>
                        <Button size="sm" onClick={() => document.getElementById('book-file-upload')?.click()}>
                          Upload New
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => {
                          setUpdateData(prev => ({ ...prev, bookFile: undefined }));
                          setPreviewUrls(prev => {
                            const newUrls = { ...prev };
                            delete newUrls.bookFile;
                            return newUrls;
                          });
                        }}>
                          Remove
                        </Button>
                      </HStack>
                    </VStack>
                  ) : (
                    <VStack>
                      <IconWrapper icon={FiUpload} size={24} />
                      <Text>Click to upload comic/book file</Text>
                      <Text fontSize="xs" color="gray.500">Supports: PDF, EPUB, MOBI</Text>
                      <Input
                        type="file"
                        accept=".pdf,.epub,.mobi"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setUpdateData(prev => ({ ...prev, bookFile: file }));
                            setPreviewUrls(prev => ({ ...prev, bookFile: { name: file.name } }));
                          }
                        }}
                        display="none"
                        id="book-file-upload"
                      />
                      <Button size="sm" onClick={() => document.getElementById('book-file-upload')?.click()}>
                        Choose File
                      </Button>
                    </VStack>
                  )}
                </Box>
              </VStack>

              {/* Thumbnail Upload */}
              <VStack align="stretch">
                <Text fontWeight="medium">{content?.category === 'book' ? 'Book Cover' : 'Thumbnail'}</Text>
                <Box
                  border="2px dashed"
                  borderColor={borderColor}
                  borderRadius="md"
                  p={4}
                  textAlign="center"
                >
                  {previewUrls.thumbnailFile ? (
                    <VStack>
                      <Image src={typeof previewUrls.thumbnailFile === 'string' ? previewUrls.thumbnailFile : ''} maxH="200px" />
                      <HStack>
                        <Button size="sm" onClick={() => document.getElementById('thumbnail-upload')?.click()}>
                          Upload New
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => {
                          setUpdateData(prev => ({ ...prev, thumbnailFile: undefined }));
                          setPreviewUrls(prev => {
                            const newUrls = { ...prev };
                            delete newUrls.thumbnailFile;
                            return newUrls;
                          });
                        }}>
                          Remove
                        </Button>
                      </HStack>
                    </VStack>
                  ) : (
                    <VStack>
                      <IconWrapper icon={FiUpload} size={24} />
                      <Text>Click to upload {content?.category === 'book' ? 'book cover' : 'thumbnail'}</Text>
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleFileUpload('thumbnailFile', file);
                        }}
                        display="none"
                        id="thumbnail-upload"
                      />
                      <Button size="sm" onClick={() => document.getElementById('thumbnail-upload')?.click()}>
                        Choose File
                      </Button>
                    </VStack>
                  )}
                </Box>
              </VStack>
            </VStack>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSave}
            isLoading={isSaving}
            loadingText="Saving..."
            leftIcon={<IconWrapper icon={FiSave} size={16} />}
          >
            Save Changes
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EditContentModal;
